import { AxiosInstance } from 'axios';
import { BaseApiService } from './base-api.service';
import { axiosInstance } from '../axios-instance';
import { ENDPOINTS } from '../endpoint';
import {
  AuditLog,
  AuditLogFilters,
  AuditLogStats,
  PaginatedAuditLogs,
} from '../types/audit-log.types';

/**
 * Service for interacting with the Audit Log API
 */
export class AuditLogService extends BaseApiService {
  constructor(axios: AxiosInstance = axiosInstance) {
    super(ENDPOINTS.AUDIT_LOGS.BASE, axios);
  }

  /**
   * Get audit logs with filters and pagination
   * @param filters - Filter parameters
   * @returns Paginated list of audit logs
   */
  async getAuditLogs(
    filters: AuditLogFilters = {}
  ): Promise<PaginatedAuditLogs> {
    const params: any = {
      page: filters.page || 1,
      limit: filters.limit || 50,
    };

    if (filters.userId) params.userId = filters.userId;
    if (filters.action) {
      params.action = Array.isArray(filters.action) 
        ? filters.action.join(',') 
        : filters.action;
    }
    if (filters.resource) {
      params.resource = Array.isArray(filters.resource)
        ? filters.resource.join(',')
        : filters.resource;
    }
    if (filters.severity) {
      params.severity = Array.isArray(filters.severity)
        ? filters.severity.join(',')
        : filters.severity;
    }
    if (filters.category) {
      params.category = Array.isArray(filters.category)
        ? filters.category.join(',')
        : filters.category;
    }
    if (filters.startDate) params.startDate = filters.startDate;
    if (filters.endDate) params.endDate = filters.endDate;
    if (filters.search) params.search = filters.search;
    if (filters.sortBy) params.sortBy = filters.sortBy;
    if (filters.sortOrder) params.sortOrder = filters.sortOrder;

    const { data } = await this.axios.get<PaginatedAuditLogs>(
      ENDPOINTS.AUDIT_LOGS.BASE,
      { params }
    );

    // Parse dates
    data.data = data.data.map(log => ({
      ...log,
      timestamp: new Date(log.timestamp),
    }));

    return data;
  }

  /**
   * Get single audit log by ID
   * @param id - Audit log ID
   * @returns Single audit log
   */
  async getAuditLogById(id: string): Promise<AuditLog> {
    const { data } = await this.axios.get<AuditLog>(
      ENDPOINTS.AUDIT_LOGS.BY_ID(id)
    );

    // Parse date
    data.timestamp = new Date(data.timestamp);

    return data;
  }

  /**
   * Get audit log statistics
   * @param startDate - Start date filter
   * @param endDate - End date filter
   * @returns Statistics object
   */
  async getAuditStats(
    startDate?: string,
    endDate?: string
  ): Promise<AuditLogStats> {
    const params: any = {};
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;

    const { data } = await this.axios.get<AuditLogStats>(
      ENDPOINTS.AUDIT_LOGS.STATS,
      { params }
    );

    // Parse dates in recent errors
    if (data.recentErrors) {
      data.recentErrors = data.recentErrors.map(log => ({
        ...log,
        timestamp: new Date(log.timestamp),
      }));
    }

    return data;
  }

  /**
   * Export audit logs to CSV or JSON
   * @param filters - Filter parameters
   * @param format - Export format (csv or json)
   * @returns Blob containing the exported data
   */
  async exportAuditLogs(
    filters: AuditLogFilters = {},
    format: 'csv' | 'json' = 'csv'
  ): Promise<Blob> {
    const params: any = { format };

    if (filters.userId) params.userId = filters.userId;
    if (filters.action) {
      params.action = Array.isArray(filters.action)
        ? filters.action.join(',')
        : filters.action;
    }
    if (filters.resource) {
      params.resource = Array.isArray(filters.resource)
        ? filters.resource.join(',')
        : filters.resource;
    }
    if (filters.severity) {
      params.severity = Array.isArray(filters.severity)
        ? filters.severity.join(',')
        : filters.severity;
    }
    if (filters.category) {
      params.category = Array.isArray(filters.category)
        ? filters.category.join(',')
        : filters.category;
    }
    if (filters.startDate) params.startDate = filters.startDate;
    if (filters.endDate) params.endDate = filters.endDate;
    if (filters.search) params.search = filters.search;

    const { data } = await this.axios.get(
      ENDPOINTS.AUDIT_LOGS.EXPORT,
      {
        params,
        responseType: 'blob',
      }
    );

    return data;
  }

  /**
   * Get related logs (context before/after)
   * @param logId - Audit log ID
   * @param context - Number of logs to fetch before/after
   * @returns Array of related audit logs
   */
  async getRelatedLogs(
    logId: string,
    context: number = 5
  ): Promise<AuditLog[]> {
    const { data } = await this.axios.get<AuditLog[]>(
      ENDPOINTS.AUDIT_LOGS.RELATED(logId),
      { params: { context } }
    );

    // Parse dates
    return data.map(log => ({
      ...log,
      timestamp: new Date(log.timestamp),
    }));
  }

  /**
   * Get Prometheus metrics
   * @returns Metrics in Prometheus format
   */
  async getMetrics(): Promise<string> {
    const { data } = await this.axios.get<string>(
      ENDPOINTS.AUDIT_LOGS.METRICS
    );
    return data;
  }
}

// Export singleton instance
export const auditLogService = new AuditLogService();
